import { useEffect, useRef, useState } from 'react';
import type { Settings } from '@core/settings/schema.ts';
import type { VideoController } from '@ports/VideoController.ts';
import type { SubtitleSlot, SubtitleStore } from '../state/subtitleStore.ts';
import type { SlotSyncView } from '../sync/subtitleSyncEngine.ts';
import { getRenderText } from '../core/renderText.ts';
import { initVoices, readerBridge, resetReaderCue } from '../features/reader/subtitleReader.ts';

export type UseSubtitleReaderOptions = {
  enabled: boolean;
  main: SlotSyncView | null;
  second: SlotSyncView | null;
  settings: Settings;
  video: VideoController | null;
  store: SubtitleStore;
};

/**
 * Extension readerBridge: if main subtitle is selected, only speak main;
 * otherwise speak second.
 */
export function resolveReaderSlot(store: SubtitleStore): SubtitleSlot | null {
  if (store.getSlot('main').selectedId) return 'main';
  if (store.getSlot('second').selectedId) return 'second';
  return null;
}

/** Prefer selected translation language (MRU[0]), else subtitle track code. */
export function resolveReaderLangCode(
  store: SubtitleStore,
  settings: Settings,
  slot: SubtitleSlot,
): string {
  const mru = slot === 'main' ? settings.mainTranslations : settings.secondTranslations;
  const translationCode = mru[0];
  if (translationCode && translationCode !== 'null') {
    return translationCode;
  }
  const state = store.getSlot(slot);
  const track = state.trackList?.subtitles.find(
    (entry) => String(entry.value) === state.selectedId,
  );
  return track?.code || 'en';
}

/**
 * Speak what is painted: extension passes getRenderText HTML into readerBridge.
 */
export function pickActiveReaderCue(
  view: SlotSyncView | null,
  speakingMode: boolean,
): { cue: SlotSyncView['cues'][number]; text: string } | null {
  if (!view || view.hidden || view.cues.length === 0) return null;
  const cue = view.cues[0];
  if (!cue) return null;
  const text = getRenderText(cue, view.dualMode, {
    loadingText: '',
    speakingMode,
  });
  if (!text || cue.loading) return null;
  return { cue, text };
}

/**
 * Speaks the active subtitle cue via Web Speech API when reader is enabled.
 * Mobile: enable from Settings turns TTS on immediately (no shortcut required).
 */
export function useSubtitleReader({
  enabled,
  main,
  second,
  settings,
  video,
  store,
}: UseSubtitleReaderOptions): void {
  const readerState = useRef({ isLoading: false }).current;
  /** Bump when voices load so enabling before voiceschanged still speaks. */
  const [voicesEpoch, setVoicesEpoch] = useState(0);
  const attemptRef = useRef(0);

  useEffect(() => {
    initVoices();
    const onVoices = () => {
      initVoices();
      setVoicesEpoch((n) => n + 1);
    };
    window.speechSynthesis?.addEventListener('voiceschanged', onVoices);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', onVoices);
  }, []);

  useEffect(() => {
    if (!enabled) {
      resetReaderCue();
      window.speechSynthesis?.cancel();
      readerState.isLoading = false;
    }
  }, [enabled, readerState]);

  useEffect(() => {
    if (!enabled || !video) return;

    const attempt = ++attemptRef.current;

    const trySpeak = () => {
      if (attempt !== attemptRef.current) return;
      const slot = resolveReaderSlot(store);
      if (!slot) return;
      const view = slot === 'main' ? main : second;
      const active = pickActiveReaderCue(view, Boolean(settings.speakingMode));
      if (!active) return;

      const lang = resolveReaderLangCode(store, settings, slot);
      readerBridge(active.text, active.cue, lang, enabled, readerState, settings, trySpeak);
    };

    trySpeak();
  }, [enabled, main, second, settings, video, store, readerState, voicesEpoch]);
}
